import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import UserBadge from "@/components/UserBadge";
import { publicProfile, requestFriend, fetchFriends } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Youtube, UserPlus, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function UserProfile() {
  const { user_id } = useParams();
  const { user: me } = useAuth() || {};
  const [data, setData] = useState(null);
  const [friendState, setFriendState] = useState("none"); // none|sent|received|friends|self

  useEffect(() => {
    publicProfile(user_id).then(setData).catch(() => setData({ notFound: true }));
  }, [user_id]);

  useEffect(() => {
    if (!me) return;
    if (me.user_id === user_id) { setFriendState("self"); return; }
    fetchFriends().then((f) => {
      if (f.friends.some((u) => u.user_id === user_id)) setFriendState("friends");
      else if (f.sent.some((u) => u.user_id === user_id)) setFriendState("sent");
      else if (f.received.some((u) => u.user_id === user_id)) setFriendState("received");
      else setFriendState("none");
    }).catch(() => {});
  }, [me, user_id]);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>;
  if (data.notFound) return <div className="min-h-screen flex items-center justify-center text-white font-display text-2xl">User not found</div>;

  const u = data.user;

  const send = async () => {
    try { await requestFriend(user_id); toast.success("Request sent"); setFriendState("sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-14" data-testid="public-profile">
        <div className="flex items-start gap-5">
          {u.picture ? (
            <img src={u.picture} alt="" className="h-20 w-20 rounded-full border border-white/10 object-cover" />
          ) : (
            <div className="h-20 w-20 rounded-full bg-amber/20 text-amber font-display flex items-center justify-center text-3xl">{u.name?.[0] || "?"}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-4xl text-white">{u.name}</h1>
              <UserBadge user={u} size="lg" />
            </div>
            {u.bio && <p className="text-zinc-400 font-body mt-2">{u.bio}</p>}
          </div>
          {me && friendState !== "self" && (
            friendState === "friends" ? (
              <Link to={`/messages/${user_id}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-amber/40 text-amber hover:bg-amber/10 text-sm font-heading" data-testid="message-friend-btn">
                <MessageSquare className="h-4 w-4" /> Message
              </Link>
            ) : friendState === "sent" ? (
              <span className="text-xs text-zinc-500 font-heading">Request pending</span>
            ) : friendState === "received" ? (
              <Link to="/friends" className="text-amber font-heading text-sm">Open in Friends</Link>
            ) : (
              <Button size="sm" onClick={send} className="bg-amber hover:bg-amber-hover text-obsidian font-heading" data-testid="add-friend-btn">
                <UserPlus className="h-3.5 w-3.5 mr-1" /> Add friend
              </Button>
            )
          )}
        </div>

        {data.videos?.length > 0 && (
          <section className="mt-10" data-testid="profile-videos">
            <h2 className="font-display text-2xl text-white mb-4 flex items-center gap-2"><Youtube className="h-5 w-5 text-amber" /> Submitted reviews</h2>
            <ul className="grid sm:grid-cols-2 gap-4">
              {data.videos.map((v) => (
                <li key={v.id} className="border border-white/10 rounded-md p-3 bg-obsidian-surface/60">
                  <Link to={`/title/${v.title_id}`} className="text-amber font-heading text-sm hover:underline">View title →</Link>
                  <div className="aspect-video mt-2 bg-black rounded overflow-hidden">
                    {v.platform === "youtube" ? (
                      <iframe src={v.embed_url} className="w-full h-full" allowFullScreen title="" />
                    ) : (
                      <a href={v.url} target="_blank" rel="noopener noreferrer" className="block h-full grid place-items-center text-pink-300">Open on Instagram →</a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {data.posts?.length > 0 && (
          <section className="mt-10" data-testid="profile-posts">
            <h2 className="font-display text-2xl text-white mb-4">Recent posts</h2>
            <ul className="space-y-3">
              {data.posts.map((p) => (
                <li key={p.id} className="border border-white/10 rounded-md p-4 bg-obsidian-surface/60">
                  <Link to={`/title/${p.title_id}`} className="text-amber font-heading text-xs uppercase tracking-[0.18em]">on {p.title_id}</Link>
                  <p className="text-zinc-200 mt-1 font-body">{p.body}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
